import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircle, XCircle, Warning, Clock, PaperPlaneTilt, ArrowUp, Prohibit,
  ShieldCheck, Notepad, ArrowLeft, Sparkle, ClockCounterClockwise,
} from "@phosphor-icons/react";

const STATUS_COLOR = {
  approved: "bg-green-500", pending: "bg-yellow-500", under_review: "bg-blue-500",
  hold: "bg-yellow-500", docs_requested: "bg-yellow-500",
  rejected: "bg-red-500", suspended: "bg-neutral-800",
};
const STATUS_LABEL = {
  approved: "Approved", pending: "Pending", under_review: "Under review",
  hold: "On hold", docs_requested: "Docs requested",
  rejected: "Rejected", suspended: "Suspended",
};

function Pill({ status }) {
  const c = STATUS_COLOR[status] || "bg-neutral-400";
  const l = STATUS_LABEL[status] || status;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest text-white ${c}`}><span className="w-1.5 h-1.5 rounded-full bg-white/80" />{l}</span>;
}

function StatusIcon({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? "text-green-700" : "text-neutral-500"}`}>
      {ok ? <CheckCircle size={14} weight="fill" /> : <Clock size={14} weight="duotone" />} {label || (ok ? "Verified" : "Not verified")}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-brand-line p-6">
      <div className="label-eyebrow text-brand-terracotta mb-4">{title}</div>
      {children}
    </section>
  );
}

function KV({ rows }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} className="border-b border-brand-line last:border-0">
            <td className="py-2 pr-4 text-brand-muted text-[11px] uppercase tracking-widest whitespace-nowrap">{k}</td>
            <td className="py-2 text-brand-ink">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProgressBar({ pct, color = "bg-brand-terracotta" }) {
  return (
    <div className="h-1.5 bg-brand-line rounded-full overflow-hidden">
      <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

export default function AdminCandidate() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [notesText, setNotesText] = useState("");
  const [remarks, setRemarks] = useState("");
  const [messageText, setMessageText] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, a] = await Promise.all([
        api.get(`/admin/candidates/${id}`),
        api.get(`/admin/candidates/${id}/audit`),
      ]);
      setData(d.data); setAudit(a.data.log || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load candidate");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const perms = useMemo(() => new Set(data?.viewer_permissions || []), [data]);
  const can = (p) => perms.has(p);

  const doAction = async (action, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/candidates/${id}/action/${action}`, body);
      toast.success(`Action: ${action.replace("_", " ")}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Action failed: ${action}`);
    } finally { setBusy(false); setShowMessage(false); setMessageText(""); setRemarks(""); }
  };

  const addNote = async () => {
    if (!notesText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/candidates/${id}/notes`, { text: notesText.trim() });
      setNotesText(""); await load();
      toast.success("Internal note added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add note");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-12 text-brand-muted">Loading candidate dossier…</div>;
  const c = data.candidate, av = data.ai_quality, idn = data.identity, mod = data.ai_moderation, bg = data.background_checks;
  const vb = data.verification_badge;
  const fam = data.family_verification;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8" data-testid="admin-candidate-page">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-brand-muted hover:text-brand-ink inline-flex items-center gap-1"><ArrowLeft size={14} /> Back to candidates</button>

      {/* Header */}
      <div className="mt-4 bg-white border border-brand-line p-8 flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1">
          <div className="label-eyebrow text-brand-terracotta">Candidate dossier · {c.profile_id}</div>
          <h1 className="font-serif text-4xl mt-2 tracking-tight text-brand-ink">{c.name}</h1>
          <div className="mt-2 text-sm text-brand-muted">
            {c.gender} · {c.age} yrs · {c.city}{c.state ? `, ${c.state}` : ""} · Plan: {(c.plan || "free").toUpperCase()} · Created {c.created_at?.slice(0, 10)}
          </div>
        </div>
        <div className="flex flex-col items-start md:items-end gap-2">
          <Pill status={data.overall_status} />
          <div className="text-xs text-brand-muted">Signed in as <strong className="text-brand-ink">{user?.email}</strong> ({user?.role})</div>
        </div>
      </div>

      {/* Scorecards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-brand-line p-6">
          <div className="label-eyebrow text-brand-muted">Profile completion</div>
          <div className="font-serif text-5xl tracking-tighter mt-2 text-brand-ink">{c.completeness}<span className="text-2xl text-brand-muted">%</span></div>
          <div className="mt-3"><ProgressBar pct={c.completeness} /></div>
        </div>
        <div className="bg-brand-ink text-white p-6">
          <div className="text-[11px] uppercase tracking-widest text-brand-gold">AI Quality Score</div>
          <div className="font-serif text-5xl tracking-tighter mt-2">{av.score}<span className="text-2xl text-white/50">/100</span></div>
          <div className="mt-3 text-xs uppercase tracking-widest text-brand-gold">{av.verdict || "not run"}</div>
        </div>
        <div className={`p-6 ${vb.badge_awarded ? "bg-brand-terracotta text-white" : "bg-brand-bone border border-brand-line"}`}>
          <div className={`text-[11px] uppercase tracking-widest ${vb.badge_awarded ? "text-white" : "text-brand-muted"}`}>Verification badge</div>
          <div className={`font-serif text-3xl mt-2 ${vb.badge_awarded ? "text-white" : "text-brand-ink"}`}>{vb.badge_awarded ? "Awarded" : "Not yet"}</div>
          <div className={`mt-2 flex gap-1 flex-wrap text-[10px] uppercase tracking-widest ${vb.badge_awarded ? "text-white/80" : "text-brand-muted"}`}>
            {["identity", "email", "mobile", "photo", "ai_verified"].map((k) => <span key={k}>{vb[k] ? "✓" : "•"} {k}</span>)}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="mt-6 bg-white border border-brand-line p-6" data-testid="admin-actions">
        <div className="label-eyebrow text-brand-terracotta mb-3">Administrator actions</div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional remarks…" className="rounded-none border-brand-line flex-1 min-w-[240px]" data-testid="action-remarks" />
        </div>
        <div className="flex flex-wrap gap-2">
          {can("approve") && <Button disabled={busy} onClick={() => doAction("approve", { remarks })} className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid="btn-approve"><CheckCircle size={16} className="mr-1" /> Approve</Button>}
          {can("request_docs") && <Button disabled={busy} onClick={() => doAction("request_docs", { remarks })} className="rounded-full bg-yellow-500 hover:bg-yellow-600 text-white" data-testid="btn-req-docs"><Warning size={16} className="mr-1" /> Request documents</Button>}
          {can("hold") && <Button disabled={busy} onClick={() => doAction("hold", { remarks })} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-hold"><Clock size={16} className="mr-1" /> Hold</Button>}
          {can("message") && <Button disabled={busy} onClick={() => setShowMessage(!showMessage)} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-message"><PaperPlaneTilt size={16} className="mr-1" /> Send message</Button>}
          {can("escalate") && <Button disabled={busy} onClick={() => doAction("escalate", { remarks })} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-escalate"><ArrowUp size={16} className="mr-1" /> Escalate</Button>}
          {can("reject") && <Button disabled={busy} onClick={() => doAction("reject", { remarks })} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="btn-reject"><XCircle size={16} className="mr-1" /> Reject</Button>}
          {can("suspend") && <Button disabled={busy} onClick={() => doAction("suspend", { remarks })} className="rounded-full bg-neutral-900 hover:bg-black text-white" data-testid="btn-suspend"><Prohibit size={16} className="mr-1" /> Suspend</Button>}
        </div>
        {showMessage && (
          <div className="mt-3 flex gap-2">
            <Textarea rows={2} value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="Message to candidate…" className="rounded-none border-brand-line" data-testid="message-text" />
            <Button disabled={busy || !messageText.trim()} onClick={() => doAction("message", { message: messageText })} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white self-start" data-testid="btn-message-send">Send</Button>
          </div>
        )}
      </div>

      {/* Grid: Identity + Family + Employment + Education */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Identity Verification">
          <KV rows={[
            ["Mobile", <StatusIcon key="m" ok={idn.mobile?.verified} label={idn.mobile?.verified ? `Verified · ${idn.mobile.verified_at?.slice(0,10)}` : "Not verified"} />],
            ["Email", <StatusIcon key="e" ok={idn.email?.verified} label={idn.email?.verified ? `Verified · ${idn.email.verified_at?.slice(0,10)}` : "Not verified"} />],
            ["Government ID", <StatusIcon key="g" ok={idn.government_id?.verified} label={idn.government_id?.document_present ? `Doc on file · ${idn.government_id?.review_status||""}` : "Not uploaded"} />],
            ["Photograph", <StatusIcon key="p" ok={idn.photograph?.verified} label={idn.photograph?.verified ? `AI-cleared · ${idn.photograph.verified_at?.slice(0,10)}` : "Not verified"} />],
            ["Face match", <span key="fm" className="text-xs text-brand-muted italic">{idn.face_match?.note}</span>],
            ["Address proof", <span key="ap" className="text-xs text-brand-muted italic">{idn.address_proof?.note}</span>],
            ["Date of birth", <StatusIcon key="d" ok={idn.date_of_birth?.verified} label={idn.date_of_birth?.value || "Not on file"} />],
          ]} />
        </Section>

        <Section title="Family Verification">
          <KV rows={[
            ["Father", <StatusIcon key="f" ok={fam?.father?.verified} />],
            ["Mother", <StatusIcon key="m" ok={fam?.mother?.verified} />],
            ["Guardian", <StatusIcon key="g" ok={fam?.guardian?.verified} />],
            ["Wali (optional)", <span key="w" className="text-xs text-brand-muted">{fam?.wali?.verified ? "Verified" : "Optional · not verified"}</span>],
            ["Family contact", <StatusIcon key="fc" ok={fam?.family_contact?.verified} />],
          ]} />
        </Section>

        <Section title="Education Verification">
          <KV rows={[
            ["Highest qualification", <span key="q">{c.education || "—"}</span>],
            ["Verification status", <StatusIcon key="es" ok={data.education_verification?.verified} label={data.education_verification?.verified ? "Self-attested" : "Not self-attested"} />],
          ]} />
        </Section>

        <Section title="Employment Verification">
          <KV rows={[
            ["Employer / role", <span key="e">{c.profession || "—"}</span>],
            ["Income range", <span key="i">{c.annual_income || "—"}</span>],
            ["Verification status", <StatusIcon key="es" ok={data.employment_verification?.verified} label={data.employment_verification?.verified ? "Self-attested" : "Not self-attested"} />],
          ]} />
        </Section>
      </div>

      {/* AI Moderation + Background Checks */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="AI Moderation">
          <KV rows={Object.entries(mod).map(([k, v]) => [
            k.replace(/_/g, " "),
            <span key={k} className={`text-xs uppercase tracking-widest ${v?.status === "passed" || v?.status === "clear" ? "text-green-700" : v?.status === "flagged" ? "text-red-700" : "text-brand-muted italic"}`}>{typeof v === "string" ? v : v?.status || "—"}{v?.matches ? ` (${v.matches})` : ""}</span>,
          ])} />
        </Section>

        <Section title="Background Checks">
          <KV rows={Object.entries(bg).map(([k, v]) => [
            k.replace(/_/g, " "),
            <span key={k} className={`text-xs uppercase tracking-widest ${v?.status === "clear" ? "text-green-700" : v?.status === "flagged" ? "text-red-700" : "text-brand-muted italic"}`}>{v?.status}{v?.count ? ` (${v.count})` : ""}</span>,
          ])} />
        </Section>
      </div>

      {/* Internal Notes */}
      <div className="mt-6">
        <Section title="Internal Notes · staff-only">
          <div className="space-y-2 mb-4">
            {(data.internal_notes || []).length === 0 && <div className="text-sm text-brand-muted italic">No notes yet.</div>}
            {(data.internal_notes || []).map((n) => (
              <div key={n.id} className="bg-brand-bone border border-brand-line p-3">
                <div className="text-xs text-brand-muted flex items-center gap-2"><Notepad size={12} /> {n.author_email} ({n.author_role}) · {n.at?.slice(0, 16).replace("T", " ")}</div>
                <div className="text-sm text-brand-ink mt-1 whitespace-pre-wrap">{n.text}</div>
              </div>
            ))}
          </div>
          {can("notes") && (
            <div className="flex gap-2">
              <Textarea rows={2} value={notesText} onChange={(e) => setNotesText(e.target.value)} placeholder="Add a private note visible only to staff…" className="rounded-none border-brand-line" data-testid="internal-note-text" />
              <Button disabled={busy || !notesText.trim()} onClick={addNote} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white self-start" data-testid="btn-add-note">Add note</Button>
            </div>
          )}
        </Section>
      </div>

      {/* Audit trail */}
      <div className="mt-6">
        <Section title="Audit Trail">
          <div className="space-y-1 max-h-[400px] overflow-auto">
            {audit.length === 0 && <div className="text-sm text-brand-muted italic">No audit events yet.</div>}
            {audit.map((r) => (
              <div key={r.id} className="text-xs flex items-center gap-3 py-2 border-b border-brand-line last:border-0">
                <ClockCounterClockwise size={12} className="text-brand-muted" />
                <span className="text-brand-muted whitespace-nowrap">{r.at?.slice(0, 16).replace("T", " ")}</span>
                <span className="uppercase tracking-widest text-brand-terracotta text-[10px]">{r.action}</span>
                <span className="text-brand-ink flex-1 truncate">{r.actor_email} ({r.actor_role})</span>
                {r.details?.remarks && <span className="text-brand-muted italic truncate max-w-[300px]">&ldquo;{r.details.remarks}&rdquo;</span>}
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* AI Recommendation */}
      {av.highlights?.length > 0 && (
        <div className="mt-6">
          <Section title="AI Recommendation">
            <div className="flex items-center gap-2 mb-3">
              <Sparkle size={16} weight="fill" className="text-brand-gold" />
              <span className="text-brand-ink font-medium">Confidence: {av.score}% · Verdict: {av.verdict || "not run"}</span>
            </div>
            {av.highlights?.length > 0 && <div className="mb-3"><div className="text-[10px] uppercase tracking-widest text-brand-muted mb-1">Highlights</div><ul className="list-disc pl-5 text-sm text-brand-ink space-y-1">{av.highlights.map((h, i) => <li key={`h-${i}-${h.slice(0,20)}`}>{h}</li>)}</ul></div>}
            {av.suggestions?.length > 0 && <div className="mb-3"><div className="text-[10px] uppercase tracking-widest text-brand-muted mb-1">Suggestions</div><ul className="list-disc pl-5 text-sm text-brand-ink space-y-1">{av.suggestions.map((s, i) => <li key={`s-${i}-${s.slice(0,20)}`}>{s}</li>)}</ul></div>}
            {av.red_flags?.length > 0 && <div className="bg-red-50 border border-red-200 p-3"><div className="text-[10px] uppercase tracking-widest text-red-700 mb-1">Red flags</div><ul className="list-disc pl-5 text-sm text-red-900 space-y-1">{av.red_flags.map((r, i) => <li key={`rf-${i}-${r.slice(0,20)}`}>{r}</li>)}</ul></div>}
          </Section>
        </div>
      )}
    </div>
  );
}
